import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { Answer, Directive, Question, Standard } from '../domain/types';
import { SCOPING_QUESTIONS } from '../data/questions';

export interface ProductScopingState {
  // Current state
  currentStep: number;
  answers: Record<string, Answer>;
  applicableDirectives: Directive[];
  applicableStandards: Standard[];

  // Actions
  setCurrentStep: (step: number) => void;
  addAnswer: (questionId: string, value: any) => void;
  updateAnswer: (questionId: string, value: any) => void;
  clearAnswers: () => void;
  setApplicableDirectives: (directives: Directive[]) => void;
  setApplicableStandards: (standards: Standard[]) => void;
  reset: () => void;
  loadProject: (projectId: string) => void;
  // Computed
  getProgress: () => number;
  getCurrentAnswers: () => Answer[];
  isQuestionVisible: (question: Question) => boolean;
  getVisibleQuestions: () => Question[];
}

const initialState = {
  currentStep: 0,
  answers: {},
  applicableDirectives: [],
  applicableStandards: [],
};

const getVisibleQuestions = (answers: Record<string, Answer>): Question[] => {
  return SCOPING_QUESTIONS.filter((question) => {
    if (!question.dependsOn) return true;

    const dependentAnswer = answers[question.dependsOn]?.value;
    if (dependentAnswer === undefined) return false;

    if (Array.isArray(question.dependsOnValue)) {
      return question.dependsOnValue.includes(dependentAnswer);
    }

    return dependentAnswer === question.dependsOnValue;
  });
};

export const useProductScopingStore = create<ProductScopingState>()(
  persist(
    (set, get) => ({
      ...initialState,

      setCurrentStep: (step: number) => set({ currentStep: step }),

      addAnswer: (questionId: string, value: any) => {
        const answers = get().answers;
        set({
          answers: {
            ...answers,
            [questionId]: {
              questionId,
              value,
              timestamp: new Date(),
            },
          },
        });
      },

      updateAnswer: (questionId: string, value: any) => {
        const answers = get().answers;
        set({
          answers: {
            ...answers,
            [questionId]: {
              questionId,
              value,
              timestamp: new Date(),
            },
          },
        });
      },

      clearAnswers: () => set({ answers: {} }),

      setApplicableDirectives: (directives: Directive[]) =>
        set({ applicableDirectives: directives }),

      setApplicableStandards: (standards: Standard[]) => set({ applicableStandards: standards }),

      reset: () => set(initialState),

      loadProject: (projectId: string) => {
        console.log('Loading project in store:', projectId);
        // Placeholder for future implementation
      },
      getVisibleQuestions: () => {
        return getVisibleQuestions(get().answers);
      },
      getProgress: () => {
        const { answers, currentStep } = get();

        // Get the actual number of visible questions
        const visibleQuestions = getVisibleQuestions(answers);
        const totalVisibleQuestions = visibleQuestions.length;

        if (totalVisibleQuestions === 0) return 0;

        const answeredQuestions = Object.keys(answers).length;
        const stepProgress = ((currentStep + 1) / totalVisibleQuestions) * 100;

        // Calculate weighted progress based on question importance
        let weightedProgress = 0;
        const importantQuestions = [
          'product_type',
          'uses_electricity',
          'transmits_radio_waves',
          'market_date',
        ];
        const answeredImportant = importantQuestions.filter((q) => answers[q]).length;
        const importantWeight = (answeredImportant / importantQuestions.length) * 40; // 40% weight for important questions

        const regularWeight = (answeredQuestions / totalVisibleQuestions) * 60; // 60% weight for regular questions

        weightedProgress = Math.round(importantWeight + regularWeight);

        // Ensure progress doesn't exceed 100%
        const finalProgress = Math.min(Math.max(weightedProgress, Math.round(stepProgress)), 100);

        return finalProgress;
      },

      getCurrentAnswers: () => {
        const { answers } = get();
        return Object.values(answers);
      },

      isQuestionVisible: (question: Question) => {
        if (!question.dependsOn) return true;

        const { answers } = get();
        const dependentAnswer = answers[question.dependsOn];

        if (!dependentAnswer) return false;

        return dependentAnswer.value === question.dependsOnValue;
      },
    }),
    {
      name: 'ce-marking-product-scoping',
      partialize: (state) => ({
        currentStep: state.currentStep,
        answers: state.answers,
        applicableDirectives: state.applicableDirectives,
        applicableStandards: state.applicableStandards,
      }),
    },
  ),
);
